import { ReviewItem } from '../types';
import { safeNegativeId } from '../utils/idUtils';

export interface OfflineOnDemandItemMutationInput {
  name?: string;
  description?: string;
  categoryId?: number;
  questionsSchema?: any;
}

export interface OfflineOnDemandItemMutationDatabase {
  createOfflineOnDemandItem(
    reviewId: number,
    name?: string,
    description?: string,
    categoryId?: number,
    questionsSchema?: any
  ): Promise<{ id: number; tempId: string }>;
  updateOfflineOnDemandItem(
    itemId: number,
    updates: {
      name?: string;
      description?: string;
      category_id?: number;
      questions_schema?: any;
    }
  ): Promise<void>;
  getOfflineOnDemandItem(itemId: number): Promise<any | null>;
  updateReview(reviewId: number, updates: { syncStatus: 'pending' }): Promise<void>;
}

export class OfflineOnDemandItemMutationService {
  constructor(
    private readonly database: OfflineOnDemandItemMutationDatabase,
    private readonly enqueueReviewUpdate: (reviewId: number) => void
  ) {}

  async createOfflineOnDemandItem(
    reviewId: number,
    data: OfflineOnDemandItemMutationInput
  ): Promise<number> {
    const result = await this.database.createOfflineOnDemandItem(
      reviewId,
      data.name,
      data.description,
      data.categoryId,
      data.questionsSchema
    );

    await this.markReviewPendingForSync(reviewId);

    return safeNegativeId(result.tempId);
  }

  async updateOfflineOnDemandItem(
    reviewId: number,
    itemId: number,
    data: OfflineOnDemandItemMutationInput
  ): Promise<ReviewItem> {
    await this.database.updateOfflineOnDemandItem(itemId, {
      name: data.name,
      description: data.description,
      category_id: data.categoryId,
      questions_schema: data.questionsSchema,
    });

    await this.markReviewPendingForSync(reviewId);

    const updatedItem = await this.database.getOfflineOnDemandItem(itemId);
    if (!updatedItem) {
      throw new Error(`On-demand item ${itemId} not found after update`);
    }

    return this.mapOfflineItemToReviewItem(reviewId, updatedItem);
  }

  private async markReviewPendingForSync(reviewId: number): Promise<void> {
    await this.database.updateReview(reviewId, {
      syncStatus: 'pending',
    });

    this.enqueueReviewUpdate(reviewId);
  }

  private mapOfflineItemToReviewItem(reviewId: number, item: any): ReviewItem {
    const itemIdToUse = item.temp_id?.startsWith('synced_')
      ? parseInt(item.temp_id.replace('synced_', ''), 10)
      : safeNegativeId(item.temp_id);

    return {
      id: itemIdToUse,
      categoryId: item.category_id || 0,
      name: item.name,
      description: item.description,
      questionsSchema: item.questions_schema || {},
      displayOrder: 999,
      isRequired: false,
      defaultPresent: false,
      isOnDemand: true,
      homeReviewId: reviewId,
      tempId: item.temp_id,
    };
  }
}
